// src/SupportCenterLanding.js
import React, { useEffect, useState } from "react";

/**
 * SupportCenterLanding
 * Production-grade, corporate-modern support landing page.
 * Tailwind CSS required (project should already include Tailwind).
 *
 * Brand colors (from user):
 *  - Primary Teal:  #307A91
 *  - Teal Variant1: #348299
 *  - Teal Variant2: #317D95
 *  - Accent Orange: #FE7F50
 *  - Light Peach (20% opacity): #FF7F4C @ 0.20
 *
 * Usage:
 *  - Place this file at src/SupportCenterLanding.js
 *  - Import in App.js: import SupportCenterLanding from './SupportCenterLanding';
 *  - Ensure Tailwind CSS is configured and Poppins or system font is available.
 */

const BRAND = {
  teal: "#307A91",
  teal1: "#348299",
  teal2: "#317D95",
  accent: "#FE7F50",
  peach20: "rgba(255,127,76,0.20)",
  neutralText: "#0f1724",
};

const ExampleTag = ({ children }) => (
  <span className="inline-block text-xs font-medium bg-white/10 text-white/85 px-3 py-1 rounded-md">
    {children}
  </span>
);

export default function SupportCenterLanding() {
  const [heroShrunk, setHeroShrunk] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    // shrink hero when scroll passes threshold, enabling sticky mini-hero
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setHeroShrunk(y > 120);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // open first faq by default
    setOpenFaq(1);
  }, []);

  const toggleFaq = (id) => setOpenFaq((cur) => (cur === id ? 0 : id));

  return (
    <div className="min-h-screen bg-slate-50 text-[14px] antialiased">
      {/* Dark Blue Header */}
      <header 
        className="w-full py-4 sticky top-0 z-50"
        style={{ backgroundColor: '#1a365d' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-2xl font-bold text-white text-center">
            Helpdesk Support Portal
          </h1>
        </div>
      </header>

      {/* Navigation Bar */}
      <nav className="w-full bg-white border-b border-gray-200 sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: '#307A91' }}
              >
                U
              </div>
              <svg className="w-5 h-5" style={{ color: '#307A91' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#docs" className="text-sm hover:underline" style={{ color: '#333333' }}>Hors</a>
              <a href="#videos" className="text-sm hover:underline" style={{ color: '#333333' }}>Bodescs</a>
              <a href="#faq" className="text-sm hover:underline" style={{ color: '#333333' }}>WanMont</a>
              <a href="#" className="text-sm hover:underline" style={{ color: '#333333' }}>Accessbillity</a>
            </nav>
            <div className="flex items-center gap-3">
              <a
                href="/signin"
                className="px-4 py-2 rounded-md font-medium transition bg-white text-slate-900 hover:bg-gray-100"
              >
                Login
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero (sticky small when scrolled) - with students in library background */}
      <section
        className="relative overflow-hidden"
        aria-label="Hero"
        style={{ minHeight: heroShrunk ? '200px' : '600px' }}
      >
        {/* Background Image - Students in library (6 students around table with laptops) */}
         <img
           src="/landing_page_image.avif"
           alt="Students in library"
           className="absolute inset-0 w-full h-full object-cover"
           style={{ objectPosition: 'center 15%' }}
         />
        <div className="absolute inset-0">
          {/* Very subtle transparent overlay - keeps faces visible */}
          <div 
            className="absolute inset-0 flex items-end z-10"
            style={{
              background: 'linear-gradient(to bottom, transparent 0%, transparent 50%, rgba(255,255,255,0.2) 70%, rgba(255,255,255,0.4) 85%, rgba(255,255,255,0.5) 100%)'
            }}
          >
            {/* Hero Content - Positioned on the overlay at bottom with text shadow for readability */}
            <div
              className={`relative z-20 w-full max-w-7xl mx-auto px-6 lg:px-8 transition-all duration-300 ${
                heroShrunk ? "py-4" : "py-12 pb-16 lg:pb-24"
              }`}
            >
              <div className="flex flex-col lg:flex-row items-center gap-8">
                {/* Left: hero text */}
                <div className="flex-1 text-center lg:text-left">
                  <h1
                    className={`font-extrabold leading-tight transition-all duration-300 ${
                      heroShrunk ? "text-2xl" : "text-4xl lg:text-5xl"
                    }`}
                    style={{ 
                      letterSpacing: "-0.02em",
                      color: '#1a1a1a',
                      textShadow: '0 2px 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,255,255,0.5)'
                    }}
                  >
                    Support Center
                  </h1>
                  <p 
                    className={`mt-3 max-w-xl ${heroShrunk ? "text-sm" : "text-lg"}`}
                    style={{ 
                      color: '#2d2d2d',
                      textShadow: '0 1px 3px rgba(255,255,255,0.8), 0 0 6px rgba(255,255,255,0.4)'
                    }}
                  >
                    Clear documentation, guided videos and timely support — built with your brand in mind.
                  </p>

                  {/* Search */}
                  <div className={`mt-6 max-w-2xl mx-auto lg:mx-0 transition-opacity duration-300 ${heroShrunk ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                    <div className="flex items-center bg-white rounded-full shadow-sm overflow-hidden">
                      <input
                        aria-label="Search knowledge base"
                        placeholder="Search for articles, error codes, or keywords..."
                        className="flex-1 px-4 py-3 text-sm text-slate-700 outline-none"
                      />
                      <div className="px-3 border-l border-slate-100">
                        <select className="bg-transparent text-sm outline-none">
                          <option>All categories</option>
                          <option>Getting started</option>
                          <option>API</option>
                        </select>
                      </div>
                      <button
                        className="px-5 py-2 font-semibold rounded-r-full"
                        style={{
                          background: BRAND.accent,
                          color: "white",
                        }}
                      >
                        Search
                      </button>
                    </div>
                    <div className="mt-3 flex gap-3 text-xs justify-center lg:justify-start">
                      <span className="inline-block text-xs font-medium bg-gray-800/10 text-gray-800 px-3 py-1 rounded-md">Popular</span>
                      <span className="inline-block text-xs font-medium bg-gray-800/10 text-gray-800 px-3 py-1 rounded-md">API</span>
                      <span className="inline-block text-xs font-medium bg-gray-800/10 text-gray-800 px-3 py-1 rounded-md">Account</span>
                      <span className="inline-block text-xs font-medium bg-gray-800/10 text-gray-800 px-3 py-1 rounded-md">Billing</span>
                    </div>
                  </div>
                </div>

                {/* Right: decorative panel with 3 cards */}
                <div className="w-full lg:w-2/5">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 lg:p-6 shadow-md border border-white/20">
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        { title: "Knowledge Base", desc: "Comprehensive guides & examples", icon: "01" },
                        { title: "Contact Support", desc: "Open a ticket for personalized help", icon: "02" },
                        { title: "Community", desc: "Join discussions and share solutions", icon: "03" },
                      ].map((c) => (
                        <div key={c.title} className="flex items-start gap-4 bg-white rounded-lg p-3 shadow-sm">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold" style={{ background: `linear-gradient(180deg, ${BRAND.teal1}, ${BRAND.teal})` }}>
                            {c.icon}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-800">{c.title}</div>
                            <div className="text-sm text-slate-500 mt-1">{c.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* floating, subtle shapes */}
                  <div aria-hidden className="mt-4 flex gap-3 justify-end">
                    <svg width="96" height="36" viewBox="0 0 96 36" fill="none" className="opacity-80">
                      <rect rx="8" width="28" height="28" x="0" y="4" fill={BRAND.accent} />
                      <circle cx="58" cy="18" r="14" fill={BRAND.teal1} />
                    </svg>
                    <svg width="40" height="40" viewBox="0 0 40 40" className="opacity-70">
                      <circle cx="20" cy="20" r="18" fill={BRAND.teal2} />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Curve SVG to create smooth transition to white canvas */}
        <div className="relative -mb-1">
          <svg viewBox="0 0 1440 120" className="w-full block" preserveAspectRatio="none">
            <path d="M0 40 C220 110 360 0 720 60 C1080 120 1220 40 1440 80 L1440 120 L0 120 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Main content canvas */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 -mt-6 pb-20">
        {/* Category cards (grid) */}
        <section id="docs" className="mt-6">
          <h2 className="text-2xl font-semibold text-slate-800">Documentation Categories</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">Browse guides, API references, release notes and community resources.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {[
              { title: "Getting started", items: 8 },
              { title: "API & Integrations", items: 12 },
              { title: "UI Components", items: 6 },
              { title: "Security", items: 4 },
              { title: "Release Notes", items: 14 },
              { title: "Community", items: 9 },
            ].map((cat) => (
              <article key={cat.title} className="bg-white rounded-2xl shadow-sm p-5 border">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-slate-800">{cat.title}</h3>
                    <ul className="mt-3 text-sm text-slate-500 space-y-1">
                      <li>Overview</li>
                      <li>Guides</li>
                      <li>Examples</li>
                    </ul>
                  </div>
                  <div className="text-xs text-slate-400">{cat.items} topics</div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Two column: Video list + Sidebar */}
        <section id="videos" className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800">Video Tutorials</h3>
              <a className="text-sm text-slate-600" href="#">See all videos →</a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="w-full h-44 bg-slate-100 flex items-center justify-center text-slate-400">Video {i}</div>
                  <div className="p-4">
                    <h4 className="font-semibold text-slate-800">Quick Walkthrough #{i}</h4>
                    <p className="text-sm text-slate-500 mt-1">Concise steps and key takeaways for common tasks.</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <h4 className="font-semibold text-slate-800">Quick Links</h4>
              <p className="text-sm text-slate-500 mt-1">Shortcuts to commonly used docs & resources</p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex items-center gap-3">
                  <svg width="36" height="36" viewBox="0 0 36 36" className="flex-none"><circle cx="18" cy="18" r="16" fill={BRAND.teal1} /></svg>
                  <div>
                    <div className="font-medium text-slate-800">API Reference</div>
                    <div className="text-xs text-slate-500">Endpoints, rate limits</div>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <svg width="36" height="36" viewBox="0 0 36 36" className="flex-none"><rect rx="6" width="36" height="36" fill={BRAND.accent} /></svg>
                  <div>
                    <div className="font-medium text-slate-800">Release Notes</div>
                    <div className="text-xs text-slate-500">Latest changes & fixes</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-4">
              <h4 className="font-semibold text-slate-800">Testimonials</h4>
              <p className="text-sm text-slate-500">“Reliable docs and great support helped our team onboard quickly.”</p>
              <div className="mt-3 flex -space-x-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-400 border-2 border-white"></div>
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-pink-400 to-red-400 border-2 border-white"></div>
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-yellow-300 to-orange-400 border-2 border-white"></div>
              </div>
            </div>
          </aside>
        </section>

        {/* FAQ */}
        <section id="faq" className="mt-10">
          <h3 className="text-2xl font-semibold text-slate-800">Frequently Asked Questions</h3>
          <p className="text-sm text-slate-500 mt-2">Helpful answers to common questions.</p>

          <div className="mt-6 space-y-4 max-w-3xl">
            {[
              {
                q: "How do I activate my account?",
                a: "After registering, check the email we sent. Click the activation link to verify your address and enable your account.",
              },
              {
                q: "How do I reset my password?",
                a: "Use the 'Forgot password' link on the sign-in screen. We'll send a secure reset link to your registered email.",
              },
              {
                q: "Where can I find API docs?",
                a: "Visit the API Reference in the Quick Links section or search for 'API' in the docs to see endpoints and examples.",
              },
            ].map((f, idx) => {
              const id = idx + 1;
              const open = openFaq === id;
              return (
                <article key={f.q} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <button
                    onClick={() => toggleFaq(id)}
                    aria-expanded={open}
                    className="w-full px-4 py-4 flex items-center justify-between text-left"
                  >
                    <div>
                      <div className="font-medium text-slate-800">{f.q}</div>
                      <div className="text-sm text-slate-500 mt-1">{open ? "Open" : "Quick answer"}</div>
                    </div>
                    <div className="flex items-center">
                      <svg className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-45" : "rotate-0"}`} viewBox="0 0 20 20" fill="none">
                        <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </button>
                  <div style={{ maxHeight: open ? "420px" : "0px", transition: "max-height 300ms ease" }} className="px-4">
                    <div className="pb-4 text-sm text-slate-600">{f.a}</div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-12 border-t pt-8 pb-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md text-white flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BRAND.teal1}, ${BRAND.teal})` }}>
                SC
              </div>
              <div>
                <div className="font-medium text-slate-800">Support Center</div>
                <div className="text-sm text-slate-500">Docs & help resources</div>
              </div>
            </div>

            <div className="text-sm text-slate-500">
              © {new Date().getFullYear()} Support Center — Built with your brand palette.
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
